import {
  DeleteOutlined,
  DollarCircleFilled,
  PlusCircleFilled,
} from "@ant-design/icons";
import { Button, Input } from "antd";
import { DownloadIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useCreateMultiExpense } from "../../api/api";
import {
  useGetAccounts,
  useGetCategories,
  useGetCategoriesOnly,
} from "../../api/api.get";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { IAccount } from "../../types/types";
import { capitalizeFirst } from "../../utils/functions";
import ImportExpensesModal from "./components/import-expenses.modal";
import dayjs from "dayjs";

export default function CreateExpensePage() {
  const { defaultGroupId } = useGroupContext();

  const navigate = useNavigate();

  // Sample monthly spending trend

  const { data: accountsData } = useGetAccounts();
  const { currency } = useCurrencyContext();

  const { notification, notifyError, notifySuccess, notifyWarn } =
    useNotifications();

  const { mutateAsync: createMultiExpenseAsync } =
    useCreateMultiExpense(defaultGroupId);

  const { data: categoryData } = useGetCategoriesOnly();
  const { data: categoryToSubCatMap } = useGetCategories();

  const [expenses, setExpenses] = useState([
    {
      id: 1,
      category: "",
      subCategory: "",
      amount: "",
      comments: "",
      date: "",
      card: "",
    },
  ]);

  const [nextId, setNextId] = useState(2);

  // Turns on red highlighting for empty required fields after a failed submit.
  const [showErrors, setShowErrors] = useState(false);

  const handleInputChange = (id: number, field: any, value: string) => {
    setExpenses((prev) =>
      prev.map((expense) => {
        if (expense.id === id) {
          return {
            ...expense,
            [field]: value,
            ...(field === "category" && { subCategory: "" }),
          };
        }
        return expense;
      }),
    );
  };

  const addExpenseRow = () => {
    if (expenses.length < 10) {
      setExpenses((prev) => [
        ...prev,
        {
          id: nextId,
          category: "",
          subCategory: "",
          amount: "",
          comments: "",
          date: "",
          card: "",
        },
      ]);
      setNextId((prev) => prev + 1);
    }
  };

  const removeExpenseRow = (id: number) => {
    if (expenses.length > 1) {
      setExpenses((prev) => prev.filter((expense) => expense.id !== id));
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    // Validate all required fields
    const allValid = expenses.every(
      (exp) => exp.category && exp.subCategory && exp.amount && exp.date,
    );

    if (!allValid) {
      setShowErrors(true);
      notifyError(
        "Please fill in category, sub category, amount and date for each expense.",
      );
      return;
    }

    setShowErrors(false);

    createMultiExpenseAsync(
      expenses.map((e) => ({
        amount: +e.amount,
        category: e.category,
        comments: e.comments,
        subcategory: e.subCategory,
        creditcard: e.card,
        expenseDate: e.date,
      })),
    )
      .then((resp) => {
        if (resp.length < expenses.length) {
          notifyWarn("Expenses saved. Shown entries are duplicates.");
          // remove the ones that is received from the payload.
          let copy = [...expenses];
          const existingKeys = new Set(
            resp.map(
              (e) =>
                `${e.category.toLowerCase()}|${e.subcategory.toLowerCase()}|${e.amount}`,
            ),
          );
          copy = copy.filter(
            (element) =>
              !existingKeys.has(
                `${element.category.toLowerCase()}|${element.subCategory.toLowerCase()}|${element.amount}`,
              ),
          );

          setExpenses([...copy]);
        } else {
          setExpenses([]);
          notifySuccess({
            title: "Success",
            description: "Expenses created successfully.",
          });
        }
      })
      .catch((err: any) => {
        if (err.response?.data?.statusCode === 409) {
          notifyError("The expenses you are trying to create already exists.");
        } else {
          notifyError("Whoops! Please try again.");
        }
      });
  };

  const handleCancel = () => {
    navigate("/home");
  };

  const totalAmount = expenses.reduce(
    (sum, exp) => sum + (parseFloat(exp.amount) || 0),
    0,
  );
  const filledExpenses = expenses.filter(
    (exp) => exp.category && exp.subCategory && exp.amount && exp.date,
  ).length;

  const [showImportDialog, setShowImportDialog] = useState<boolean>(false);

  return (
    <div className="min-h-screen mx-auto max-w-7xl">
      {notification}

      {/* Main Content */}
      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {showImportDialog && (
            <ImportExpensesModal
              open={showImportDialog}
              onSuccess={(data) => {
                setExpenses(
                  data.map((d, i) => {
                    return {
                      amount: `${d.amount}`,
                      category: "MISC",
                      comments: d.comments,
                      date: dayjs(d.expenseDate).format("YYYY-MM-DD"),
                      subCategory: "MISC",
                      card: "",
                      id: i,
                    };
                  }),
                );
                setShowImportDialog(false);
              }}
              onCancel={() => setShowImportDialog(false)}
            />
          )}
          <div className="flex flex-row justify-between items-center">
            {/* Header Section */}
            <div className="flex flex-col justiify-start items-start mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-gradient-to-br from-primary to-indigo-600 rounded-xl">
                  <DollarCircleFilled className="w-6 h-6 text-purple-600 text-4xl" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    Create Expense
                  </h1>
                  <p className="text-muted-foreground">
                    Add up to 50 expenses at once
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                icon={<PlusCircleFilled />}
                onClick={() => navigate(`/misc`)}
                className="border border-blue-600 text-blue-500"
              >
                One Time Expense
              </Button>

              <Button
                icon={<DownloadIcon style={{ width: 15 }} />}
                onClick={() => setShowImportDialog(true)}
                className="border border-blue-600 bg-blue-500 text-white"
              >
                Upload From CSV
              </Button>
            </div>
          </div>
          {/* Back Button */}

          {/* Form Card */}
          <Card className="bg-white border-0 shadow-lg mb-6">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-slate-200">
              <CardTitle className="flex items-center gap-2">
                Bulk Expense Entry
              </CardTitle>
              <CardDescription>
                Add multiple expenses at once. You can add up to{" "}
                {10 - expenses.length} more
                {10 - expenses.length === 1 ? " expense" : " expenses"}.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Expense Rows */}
                <div className="space-y-4">
                  {expenses.map((expense, index) => {
                    const subCategories = expense.category
                      ? (categoryToSubCatMap?.[expense.category] ?? [])
                      : [];

                    return (
                      <div
                        key={expense.id}
                        className="p-5 border-2 border-slate-200 rounded-lg bg-slate-50 hover:border-primary/50 transition"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-foreground text-sm">
                            Expense #{index + 1}
                          </h3>
                          <button
                            type="button"
                            onClick={() => removeExpenseRow(expense.id)}
                            disabled={expenses.length === 1}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            title="Delete this expense"
                          >
                            <DeleteOutlined className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Grid Layout */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                          {/* Category */}
                          <div className="sm:col-span-1 lg:col-span-1">
                            <label className="block text-xs font-semibold text-foreground mb-1">
                              Category *
                            </label>
                            <select
                              value={expense.category}
                              onChange={(e) =>
                                handleInputChange(
                                  expense.id,
                                  "category",
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-foreground hover:border-slate-400 transition ${
                                showErrors && !expense.category
                                  ? "border-red-500"
                                  : "border-slate-300"
                              }`}
                            >
                              <option value="">Select</option>
                              {categoryData?.map((cat) => (
                                <option key={cat} value={cat}>
                                  {cat}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Sub Category */}
                          <div className="sm:col-span-1 lg:col-span-1">
                            <label className="text-xs font-semibold text-foreground mb-1">
                              Sub Category *
                            </label>
                            <select
                              value={expense.subCategory}
                              onChange={(e) =>
                                handleInputChange(
                                  expense.id,
                                  "subCategory",
                                  e.target.value,
                                )
                              }
                              disabled={!expense.category}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-foreground hover:border-slate-400 transition disabled:bg-slate-100 disabled:text-muted-foreground disabled:cursor-not-allowed ${
                                showErrors && !expense.subCategory
                                  ? "border-red-500"
                                  : "border-slate-300"
                              }`}
                            >
                              <option value="">Select</option>
                              {subCategories.map((subCat: string) => (
                                <option key={subCat} value={subCat}>
                                  {subCat}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Amount */}
                          <div className="sm:col-span-1 lg:col-span-1">
                            <label className="text-xs font-semibold text-foreground mb-1">
                              Amount *
                            </label>
                            <div className="relative">
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={expense.amount}
                                onChange={(e) =>
                                  handleInputChange(
                                    expense.id,
                                    "amount",
                                    e.target.value,
                                  )
                                }
                                step={"0.01"}
                                className="text-sm"
                                min="0"
                                status={
                                  showErrors && !expense.amount
                                    ? "error"
                                    : undefined
                                }
                              />
                            </div>
                          </div>

                          {/* Date */}
                          <div className="sm:col-span-1 lg:col-span-1">
                            <label className="text-xs font-semibold text-foreground mb-1">
                              Date *
                            </label>
                            <Input
                              type="date"
                              value={expense.date}
                              onChange={(e) =>
                                handleInputChange(
                                  expense.id,
                                  "date",
                                  e.target.value,
                                )
                              }
                              className="text-sm"
                              status={
                                showErrors && !expense.date
                                  ? "error"
                                  : undefined
                              }
                            />
                          </div>

                          {/* Comments */}
                          <div className="sm:col-span-2 lg:col-span-1">
                            <label className="text-xs font-semibold text-foreground mb-1">
                              Notes
                            </label>
                            <Input
                              type="text"
                              placeholder="Optional notes"
                              value={expense.comments}
                              onChange={(e) =>
                                handleInputChange(
                                  expense.id,
                                  "comments",
                                  e.target.value,
                                )
                              }
                              className="text-sm"
                            />
                          </div>

                          {/* Account */}
                          <div className="sm:col-span-1 lg:col-span-1">
                            <label className="text-xs font-semibold text-foreground mb-1">
                              Account
                            </label>
                            <select
                              value={expense.card}
                              onChange={(e) =>
                                handleInputChange(
                                  expense.id,
                                  "card",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-foreground hover:border-slate-400 transition disabled:bg-slate-100 disabled:text-muted-foreground disabled:cursor-not-allowed"
                            >
                              <option value="">Cash</option>
                              {accountsData?.map((acc: IAccount) => (
                                <option key={acc.id} value={acc.id}>
                                  {capitalizeFirst(acc.name)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Expense Button */}
                {expenses.length < 50 && (
                  <button
                    type="button"
                    onClick={addExpenseRow}
                    className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg bg-gradient-to-r from-blue-100 to-blue-50 text-primary font-semibold hover:border-primary hover:bg-blue-50 transition flex items-center justify-center gap-2"
                  >
                    <PlusCircleFilled className="text-xl text-blue-400" />
                    Add Another Expense
                  </button>
                )}

                {expenses.length === 50 && (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 font-medium">
                    ℹ️ Maximum of 10 expenses reached
                  </div>
                )}

                {/* Summary Section */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-primary/20">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                      Total Expenses
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {expenses.length}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                      Completed
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {filledExpenses} / {expenses.length}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                      Total Amount
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      {currency}
                      {totalAmount.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:shadow-lg hover:from-primary/90 hover:to-indigo-600/90 transition-all duration-200 transform hover:-translate-y-0.5"
                  >
                    Create All Expenses
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 px-6 py-3 bg-slate-200 text-foreground font-semibold rounded-lg hover:bg-slate-300 transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Tips Section */}
          <Card className="bg-white border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-slate-200">
              <CardTitle className="text-lg">💡 Bulk Entry Tips</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="text-primary font-bold">1.</span>
                  <span className="text-foreground">
                    Use this form when you have multiple expenses to add at once
                    (e.g., weekly groceries, multiple receipts)
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">2.</span>
                  <span className="text-foreground">
                    All required fields (Category, Sub Category, Amount, Date)
                    must be filled for each expense
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">3.</span>
                  <span className="text-foreground">
                    You can add up to 10 expenses at a time. Click "Add Another
                    Expense" to add more rows
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-bold">4.</span>
                  <span className="text-foreground">
                    Comments/Notes are optional but helpful for tracking details
                    later
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
